import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Swal from 'sweetalert2';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid } from '@mui/x-data-grid';
import { useTheme } from '@mui/material/styles';
import ImageIcon from '@mui/icons-material/Image';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import apiClient from "@/api/client";

const EventView = () => {
    const theme = useTheme();
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isTableLoading, setIsTableLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [editingEvent, setEditingEvent] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedImageFile, setSelectedImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        location_details: '',
        organizer: '',
        category: '',
        tags: '',
        url: '',
        other_details: '',
        is_visible: true,
        admin_id: 1,
    });

    const categories = [
        'Conference',
        'Workshop',
        'Seminar',
        'Dhamma Talk',
        'Meditation Session',
        'Community Service',
        'Dana Program',
        'Arama Poojawa',
        'Cultural Event',
        'Educational Program',
        'ceremony'
    ];

    useEffect(() => {
        fetchEvents();
    }, []);

    // Helper function to get image URL - PLACE THIS HERE
    const getImageUrl = (imagePath) => {
        if (!imagePath) return '';
        
        console.log('Original image path:', imagePath);
        
        // If it's already a full URL or data URL, return as is
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://') || imagePath.startsWith('data:')) {
            return imagePath;
        }
        
        // The imagePath should be like "events/69c211fd181c6_1774326269.png"
        // Extract just the filename
        let filename = imagePath;
        if (filename.includes('/')) {
            const parts = filename.split('/');
            filename = parts[parts.length - 1];
        }
        
        // Construct the full URL
        const baseUrl = import.meta.env.VITE_BASE_URL;
        const imageUrl = `${baseUrl}/storage/events/${filename}`;
        
        console.log('Constructed URL:', imageUrl);
        return imageUrl;
    };

    // Helper function to parse images from various formats
    const parseImages = (images) => {
        if (!images) return [];
        
        // If it's already an array, return it
        if (Array.isArray(images)) return images;
        
        // If it's a string, try to parse it as JSON
        if (typeof images === 'string') {
            try {
                const parsed = JSON.parse(images);
                return Array.isArray(parsed) ? parsed : [parsed];
            } catch (e) {
                // If it's not valid JSON, it might be a single path
                return [images];
            }
        }
        
        return [];
    };

    const fetchEvents = async () => {
    try {
        setIsTableLoading(true);
        const response = await apiClient.get('/events');
        const result = response.data;
        const eventsData = result.data || result;
        
        // Parse images if they're strings
        const parsedEvents = eventsData.map(event => ({
        ...event,
        images: parseImages(event.images)
        }));
        
        setEvents(parsedEvents);
        console.log('Parsed events:', parsedEvents);
    } catch (error) {
        console.error('Error fetching events:', error);
        Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to load events. Please check your connection.'
        });
    } finally {
        setIsTableLoading(false);
    }
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
        setErrors({ ...errors, [name]: "" });
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            Swal.fire({ icon: "error", title: "Invalid File", text: "Please select an image file." });
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            Swal.fire({ icon: "error", title: "File Too Large", text: "Please select an image smaller than 5MB." });
            return;
        }

        setSelectedImageFile(file);
        
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const validateForm = () => {
        let tempErrors = {};
        let isValid = true;

        if (!formData.title.trim()) {
            tempErrors.title = "Title is required";
            isValid = false;
        }

        if (!formData.description.trim()) {
            tempErrors.description = "Description is required";
            isValid = false;
        }

        if (!formData.start_date) {
            tempErrors.start_date = "Start date is required";
            isValid = false;
        }

        if (!formData.end_date) {
            tempErrors.end_date = "End date is required";
            isValid = false;
        } else if (new Date(formData.start_date) > new Date(formData.end_date)) {
            tempErrors.end_date = "End date must be after start date";
            isValid = false;
        }

        if (!formData.location_details.trim()) {
            tempErrors.location_details = "Location is required";
            isValid = false;
        }

        if (!formData.organizer.trim()) {
            tempErrors.organizer = "Organizer is required";
            isValid = false;
        }

        if (!formData.category) {
            tempErrors.category = "Category is required";
            isValid = false;
        }

        setErrors(tempErrors);
        return isValid;
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            start_date: '',
            end_date: '',
            location_details: '',
            organizer: '',
            category: '',
            tags: '',
            url: '',
            other_details: '',
            is_visible: true,
            admin_id: 1,
        });
        setErrors({});
        setEditingEvent(null);
        setSelectedImageFile(null);
        setImagePreview('');
    };

    const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
        const isEditing = !!editingEvent;

        // Prepare payload
        const payload = {
        title: formData.title,
        description: formData.description,
        start_date: formData.start_date,
        end_date: formData.end_date,
        location_details: formData.location_details,
        organizer: formData.organizer,
        category: formData.category,
        tags: formData.tags || '',
        url: formData.url || '',
        other_details: formData.other_details || '',
        is_visible: formData.is_visible ? 1 : 0,
        admin_id: formData.admin_id,
        };

        // Handle image as base64 if selected
        if (selectedImageFile) {
                // Convert image to base64
        const base64Image = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(selectedImageFile);
        });
        payload.images = [base64Image];
        }

        let response;
        if (isEditing) {
        response = await apiClient.put(`/events/${editingEvent.id}`, payload);
        } else {
        response = await apiClient.post('/events', payload);
        }

        // Axios automatically throws on non-2xx, so if we reach here, success
        Swal.fire({
        icon: "success",
        title: "Success!",
        text: `Event ${isEditing ? "updated" : "created"} successfully!`,
        timer: 2000,
        showConfirmButton: false,
        }).then(() => {
        resetForm();
        setOpenDialog(false);
        fetchEvents();
        });
    } catch (error) {
        console.error('Submit error:', error);
        const errorMessage = error.response?.data?.message || error.message || "Failed to save event. Please try again.";
        Swal.fire({
        icon: "error",
        title: "Error!",
        text: errorMessage,
        });
    } finally {
        setIsLoading(false);
    }
    };

    const handleEdit = (item) => {
        setEditingEvent(item);
        setFormData({
            title: item.title || "",
            description: item.description || "",
            start_date: item.start_date ? item.start_date.split("T")[0] : "",
            end_date: item.end_date ? item.end_date.split("T")[0] : "",
            location_details: item.location_details || "",
            organizer: item.organizer || "",
            category: item.category || "",
            tags: item.tags || "",
            url: item.url || "",
            other_details: item.other_details || "",
            is_visible: item.is_visible === 1 ? true : false,
            admin_id: item.admin_id || 1,
        });
        setSelectedImageFile(null);

        // Parse images from the item
        const images = parseImages(item.images);
        if (images && images.length > 0) {
            const imagePath = images[0];
            const fullUrl = getImageUrl(imagePath);
            console.log('Edit - Image path:', imagePath);
            console.log('Edit - Full URL:', fullUrl);
            setImagePreview(fullUrl);
        } else {
            setImagePreview("");
        }

        setOpenDialog(true);
    };

    const handleDelete = async (eventId) => {
    const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
        try {
        await apiClient.delete(`/events/${eventId}`);
        Swal.fire('Deleted!', 'Event has been deleted.', 'success');
        fetchEvents();
        } catch (error) {
        console.error('Error:', error);
        Swal.fire('Error!', 'Failed to delete event.', 'error');
        }
    }
    };

    const toggleVisibility = async (eventId, currentVisibility) => {
    try {
        const newVisibility = currentVisibility === 1 ? 0 : 1;
        
        await apiClient.put(`/events/${eventId}`, { is_visible: newVisibility });
        
        fetchEvents();
        Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: `Event ${newVisibility === 1 ? 'shown' : 'hidden'} successfully!`,
        timer: 1500,
        showConfirmButton: false,
        });
    } catch (error) {
        console.error('Error:', error);
        Swal.fire('Error!', 'Failed to update event visibility.', 'error');
    }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Image Component for better error handling
    const EventImage = ({ imagePath, title }) => {
        const [imageError, setImageError] = useState(false);
        
        if (!imagePath) {
            return (
                <Box
                    sx={{
                        width: '40px',
                        height: '40px',
                        bgcolor: 'grey.200',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <ImageIcon sx={{ fontSize: 20, color: 'grey.500' }} />
                </Box>
            );
        }
        
        const imageUrl = getImageUrl(imagePath);
        
        if (imageError) {
            return (
                <Box
                    sx={{
                        width: '40px',
                        height: '40px',
                        bgcolor: 'grey.200',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <BrokenImageIcon sx={{ fontSize: 20, color: 'grey.500' }} />
                </Box>
            );
        }
        
        return (
            <img
                src={imageUrl}
                alt={title}
                style={{
                    width: '40px',
                    height: '40px',
                    objectFit: 'cover',
                    borderRadius: '4px'
                }}
                onError={(e) => {
                    console.error('Image failed to load:', imageUrl);
                    setImageError(true);
                }}
                onLoad={() => console.log('Image loaded successfully:', imageUrl)}
            />
        );
    };

    const columns = [
        {
            field: 'image',
            headerName: 'Image',
            width: 80,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const images = parseImages(params.row.images);
                const imagePath = images && images.length > 0 ? images[0] : null;
                return <EventImage imagePath={imagePath} title={params.row.title} />;
            },
        },
        {
            field: 'title',
            headerName: 'Title',
            width: 200,
            renderCell: (params) => (
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {params.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {params.row.description && params.row.description.length > 50
                            ? `${params.row.description.substring(0, 50)}...`
                            : params.row.description}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'category',
            headerName: 'Category',
            width: 150,
            renderCell: (params) => (
                <Chip 
                    label={params.value} 
                    size="small" 
                    sx={{ 
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff',
                        fontWeight: 500
                    }} 
                />
            ),
        },
        {
            field: 'start_date',
            headerName: 'Start Date',
            width: 120,
            valueGetter: (params) => formatDate(params.value),
        },
        {
            field: 'end_date',
            headerName: 'End Date',
            width: 120,
            valueGetter: (params) => formatDate(params.value),
        },
        {
            field: 'location_details',
            headerName: 'Location',
            width: 180,
        },
        {
            field: 'organizer',
            headerName: 'Organizer',
            width: 150,
        },
        {
            field: 'is_visible',
            headerName: 'Visible',
            width: 100,
            renderCell: (params) => (
                <Switch
                    checked={params.value === 1}
                    onChange={() => toggleVisibility(params.row.id, params.value)}
                    size="small"
                    color="primary"
                />
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Box>
                    <IconButton
                        onClick={() => handleEdit(params.row)}
                        color="primary"
                        size="small"
                        sx={{ mr: 1 }}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        onClick={() => handleDelete(params.row.id)}
                        color="error"
                        size="small"
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            {/* Header Card */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                            Event Management
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => {
                                resetForm();
                                setOpenDialog(true);
                            }}
                            sx={{ 
                                textTransform: 'none',
                                boxShadow: 2,
                                '&:hover': {
                                    boxShadow: 4
                                }
                            }}
                        >
                            + Create New Event
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Data Grid Table */}
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary', mb: 2 }}>
                        All Events
                    </Typography>
                    <Box sx={{ height: 'calc(100vh - 280px)', width: '100%' }}>
                        {isTableLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <DataGrid
                                rows={events}
                                columns={columns}
                                paginationModel={paginationModel}
                                onPaginationModelChange={setPaginationModel}
                                pageSizeOptions={[5, 10, 25, 50]}
                                disableRowSelectionOnClick
                                sx={{
                                    '& .MuiDataGrid-cell': {
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                    },
                                    '& .MuiDataGrid-columnHeaders': {
                                        backgroundColor: theme.palette.grey[100],
                                        borderBottom: `2px solid ${theme.palette.divider}`,
                                        fontWeight: 600,
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        backgroundColor: theme.palette.action.hover,
                                    },
                                }}
                                initialState={{
                                    sorting: {
                                        sortModel: [{ field: 'start_date', sort: 'desc' }],
                                    },
                                }}
                            />
                        )}
                    </Box>
                </CardContent>
            </Card>

            {/* Create/Edit Event Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
                    {editingEvent ? 'Edit Event' : 'Create New Event'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="title"
                                label="Event Title"
                                variant="outlined"
                                value={formData.title}
                                onChange={handleChange}
                                error={!!errors.title}
                                helperText={errors.title}
                                required
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth error={!!errors.category} size="small">
                                <InputLabel>Category</InputLabel>
                                <Select
                                    name="category"
                                    value={formData.category}
                                    onChange={handleChange}
                                    label="Category"
                                    required
                                >
                                    {categories.map((category) => (
                                        <MenuItem key={category} value={category}>
                                            {category}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.category && (
                                    <Typography variant="caption" color="error">
                                        {errors.category}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                name="description"
                                label="Description"
                                multiline
                                rows={3}
                                value={formData.description}
                                onChange={handleChange}
                                error={!!errors.description}
                                helperText={errors.description}
                                required
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="start_date"
                                label="Start Date"
                                type="date"
                                value={formData.start_date}
                                onChange={handleChange}
                                error={!!errors.start_date}
                                helperText={errors.start_date}
                                InputLabelProps={{ shrink: true }}
                                required
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="end_date"
                                label="End Date"
                                type="date"
                                value={formData.end_date}
                                onChange={handleChange}
                                error={!!errors.end_date}
                                helperText={errors.end_date}
                                InputLabelProps={{ shrink: true }}
                                required
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="location_details"
                                label="Location"
                                value={formData.location_details}
                                onChange={handleChange}
                                error={!!errors.location_details}
                                helperText={errors.location_details}
                                required
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="organizer"
                                label="Organizer"
                                value={formData.organizer}
                                onChange={handleChange}
                                error={!!errors.organizer}
                                helperText={errors.organizer}
                                required
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="url"
                                label="Event URL (Optional)"
                                value={formData.url}
                                onChange={handleChange}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.is_visible}
                                        onChange={(e) => setFormData({...formData, is_visible: e.target.checked})}
                                        name="is_visible"
                                    />
                                }
                                label="Event Visible"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ border: `1px dashed ${theme.palette.divider}`, borderRadius: 1, p: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Event Image
                                </Typography>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    fullWidth
                                    sx={{ mb: 2, textTransform: 'none' }}
                                    size="small"
                                >
                                    {selectedImageFile ? 'Change Image' : 'Choose Image File'}
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={handleImageChange}
                                    />
                                </Button>
                                {selectedImageFile && (
                                    <Typography variant="caption" display="block" sx={{ mb: 1, color: 'text.secondary' }}>
                                        New file: {selectedImageFile.name}
                                    </Typography>
                                )}
                                {imagePreview && (
                                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: '200px',
                                                borderRadius: '4px',
                                                border: `1px solid ${theme.palette.divider}`
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                name="tags"
                                label="Tags (Optional)"
                                placeholder="Enter tags separated by commas"
                                value={formData.tags}
                                onChange={handleChange}
                                helperText="Separate multiple tags with commas (e.g., technology, conference, innovation)"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                name="other_details"
                                label="Other Details (Optional)"
                                multiline
                                rows={2}
                                value={formData.other_details}
                                onChange={handleChange}
                                size="small"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={() => setOpenDialog(false)} variant="outlined">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} /> : null}
                    >
                        {isLoading ? (editingEvent ? 'Updating...' : 'Creating...') : (editingEvent ? 'Update Event' : 'Create Event')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EventView;